type ComparisonURLMutation = () => Promise<unknown> | unknown;

type QueuedComparisonURLMutation = {
  mutate: ComparisonURLMutation;
  reject: (reason?: unknown) => void;
  resolve: (value: unknown) => void;
};

type ReconcileComparisonURLState = {
  comparisonIsOpen: boolean;
  isLinked: boolean;
};

type ComparisonLifecycleIntent = 'exit' | 'start';

type DeferredComparisonURLStateReconciliation = ReconcileComparisonURLState & {
  lifecycleRevision: number;
};

/**
 * Creates the shared state machine that serializes comparison URL writes.
 *
 * Next Router cancels overlapping replacements, so production uses one controller
 * across every hook instance while tests can create isolated controllers.
 */
export const createPrivateAssetComparisonURLMutationController = (defaultIsLinked: boolean) => {
  const mutationQueue: QueuedComparisonURLMutation[] = [];
  let isMutationRunning = false;
  let pendingMutationCount = 0;
  let mutationsAreBlocked = false;
  let lifecycleRevision = 0;
  let latestLifecycleIntent: ComparisonLifecycleIntent | undefined;
  let latestExitPromise: Promise<unknown> | undefined;
  let linkedCommittedState: boolean | undefined;
  let linkedIntent: boolean | undefined;
  let pendingLinkedMutationCount = 0;
  let deferredURLStateReconciliation: DeferredComparisonURLStateReconciliation | undefined;

  const resetLinkedState = (isLinked: boolean | undefined) => {
    linkedCommittedState = isLinked;
    linkedIntent = isLinked;
  };

  const applyURLStateReconciliation = ({ comparisonIsOpen, isLinked }: ReconcileComparisonURLState) => {
    if (comparisonIsOpen) {
      lifecycleRevision += 1;
      latestLifecycleIntent = undefined;
      latestExitPromise = undefined;
      mutationsAreBlocked = false;
    }

    if (!mutationsAreBlocked) {
      resetLinkedState(isLinked);
    }
  };

  const applyDeferredURLStateReconciliation = () => {
    const reconciliation = deferredURLStateReconciliation;
    deferredURLStateReconciliation = undefined;

    if (!reconciliation || reconciliation.lifecycleRevision !== lifecycleRevision) {
      return;
    }

    if (latestLifecycleIntent === 'exit' && mutationsAreBlocked) {
      return;
    }

    applyURLStateReconciliation(reconciliation);
  };

  const runNextMutation = () => {
    if (isMutationRunning) {
      return;
    }

    const nextMutation = mutationQueue.shift();

    if (!nextMutation) {
      return;
    }

    isMutationRunning = true;

    const finish = () => {
      pendingMutationCount -= 1;
      isMutationRunning = false;

      if (pendingMutationCount === 0) {
        applyDeferredURLStateReconciliation();
      }

      runNextMutation();
    };

    try {
      Promise.resolve(nextMutation.mutate()).then(
        (value) => {
          nextMutation.resolve(value);
          finish();
        },
        (error: unknown) => {
          nextMutation.reject(error);
          finish();
        },
      );
    } catch (error) {
      nextMutation.reject(error);
      finish();
    }
  };

  const enqueue = (mutate: ComparisonURLMutation) => {
    const mutation = new Promise<unknown>((resolve, reject) => {
      pendingMutationCount += 1;
      mutationQueue.push({ mutate, reject, resolve });
    });

    runNextMutation();

    return mutation;
  };

  const completeLinkedMutation = (nextIsLinked: boolean, succeeded: boolean) => {
    if (succeeded) {
      linkedCommittedState = nextIsLinked;
    }

    pendingLinkedMutationCount -= 1;

    if (pendingLinkedMutationCount === 0) {
      linkedIntent = linkedCommittedState;
    }
  };

  const enqueueRegular = (mutate: ComparisonURLMutation) =>
    mutationsAreBlocked ? Promise.resolve(undefined) : enqueue(mutate);

  const enqueueDistinctExit = (mutate: ComparisonURLMutation) => {
    lifecycleRevision += 1;
    const exitRevision = lifecycleRevision;
    latestLifecycleIntent = 'exit';
    mutationsAreBlocked = true;
    const exitMutation = enqueue(() => {
      let mutationResult: Promise<unknown> | unknown;

      try {
        mutationResult = mutate();
      } catch (error) {
        if (exitRevision === lifecycleRevision) {
          latestLifecycleIntent = undefined;
          mutationsAreBlocked = false;
        }
        throw error;
      }

      return Promise.resolve(mutationResult).then(
        (value) => {
          if (exitRevision === lifecycleRevision) {
            resetLinkedState(undefined);
          }

          return value;
        },
        (error: unknown) => {
          if (exitRevision === lifecycleRevision) {
            latestLifecycleIntent = undefined;
            mutationsAreBlocked = false;
          }
          throw error;
        },
      );
    });
    latestExitPromise = exitMutation;

    void exitMutation.then(
      () => {
        if (exitRevision === lifecycleRevision && latestExitPromise === exitMutation) {
          latestExitPromise = undefined;
        }
      },
      () => {
        if (exitRevision === lifecycleRevision && latestExitPromise === exitMutation) {
          latestExitPromise = undefined;
        }
      },
    );

    return exitMutation;
  };

  const enqueueExit = (mutate: ComparisonURLMutation) => {
    if (latestLifecycleIntent === 'exit') {
      return latestExitPromise ?? Promise.resolve(undefined);
    }

    return enqueueDistinctExit(mutate);
  };

  const enqueueExitWithNavigation = (mutate: ComparisonURLMutation) => enqueueDistinctExit(mutate);

  const enqueueStart = (mutate: ComparisonURLMutation) => {
    lifecycleRevision += 1;
    const startRevision = lifecycleRevision;
    latestLifecycleIntent = 'start';
    mutationsAreBlocked = false;
    resetLinkedState(defaultIsLinked);

    return enqueue(() => {
      if (startRevision === lifecycleRevision) {
        mutationsAreBlocked = false;
        resetLinkedState(defaultIsLinked);
      }

      return mutate();
    });
  };

  const enqueueToggleLinked = (
    currentIsLinked: boolean,
    mutate: (nextIsLinked: boolean) => Promise<unknown> | unknown,
  ) => {
    if (mutationsAreBlocked) {
      return Promise.resolve(undefined);
    }

    linkedCommittedState ??= currentIsLinked;
    linkedIntent ??= linkedCommittedState;

    const nextIsLinked = !linkedIntent;
    linkedIntent = nextIsLinked;
    pendingLinkedMutationCount += 1;

    return enqueue(() => {
      let mutationResult: Promise<unknown> | unknown;

      try {
        mutationResult = mutate(nextIsLinked);
      } catch (error) {
        completeLinkedMutation(nextIsLinked, false);
        throw error;
      }

      return Promise.resolve(mutationResult).then(
        (value) => {
          completeLinkedMutation(nextIsLinked, true);
          return value;
        },
        (error: unknown) => {
          completeLinkedMutation(nextIsLinked, false);
          throw error;
        },
      );
    });
  };

  const reconcileURLState = ({ comparisonIsOpen, isLinked }: ReconcileComparisonURLState) => {
    if (pendingMutationCount > 0) {
      deferredURLStateReconciliation = { comparisonIsOpen, isLinked, lifecycleRevision };
      return;
    }

    applyURLStateReconciliation({ comparisonIsOpen, isLinked });
  };

  return {
    enqueueExit,
    enqueueExitWithNavigation,
    enqueueRegular,
    enqueueStart,
    enqueueToggleLinked,
    reconcileURLState,
  };
};
